import { Alert } from "./alert";
import { Zustandspille } from "./zustandspille";

/**
 * Eigenschaftszeile (Musterkatalog M-011 AD5, Design-Guide `eigenschaftszeile.md`): links Beschriftung mit Symbol, rechts der
 * Wert oder das Eingabeelement (aus den Formular-Bausteinen, Label versteckt). Jede Zeile speichert sofort und zeigt es an
 * (ED2): „Speichert …“ mit Drehsymbol, danach „Gespeichert“; die Anzeige steht in einer Statusregion, damit Hilfstechnik
 * sie vorliest. Fehler stehen AM Feld: ein behebbarer Wert (`fehler`, r100) als Pille mit Punkt, eine Ablehnung oder ein
 * Fehlschlag (`hinweissatz`, r105) als Hinweissatz, nie ein Toast und nie ein roher Fehlercode; beides nie zugleich.
 * Der Baustein prüft keine Rechte: ohne Recht setzt der Aufrufer einen Textwert statt eines Eingabeelements.
 */
export type SpeicherZustand = "ruhe" | "speichert" | "gespeichert";

/**
 * Spaltenaufteilung (additiv, der Standard bleibt unverändert): `standard` = Beschriftung 1 : Wert 2 (Aufgaben-Detail, M-011);
 * `breit` = Beschriftung 2 : Wert 3 ab `sm`, darunter untereinander (Beschriftung über dem Wert), für lange Beschriftungen in
 * schmaler Spalte (Serien-Detail „Wiederholung“, A6-Fund N2). `oben` (W202 V1, Kopfblock des Aufgaben-Details): Beschriftung klein ÜBER dem
 * Wert, ohne Symbol und ohne feste Spaltenbreite (ein Feld in einem Raster aus mehreren Feldern nebeneinander).
 */
export type LabelBreite = "standard" | "breit" | "fest" | "oben";

// Feste, vollständige Klassenketten (nie zusammengesetzt, R8): Aufteilung × Teil der Zeile.
const AUFTEILUNG: Record<LabelBreite, { zeile: string; label: string; wert: string; status: string; fehler: string; ganz: string; hilfe: string }> = {
  // fest (A4′, Karte „Eigenschaften“ in der rechten Spalte): Beschriftung in fester Breite (8 rem), der Wert füllt den Rest;
  // Status, Fehler und Hinweis stehen unter dem Wert, eingerückt auf die Wertspalte; Beschriftung und Wert gleich hoch, Mobil wie Desktop.
  fest: {
    zeile: "py-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5",
    label: "w-32 shrink-0 flex items-center gap-x-2 text-sm text-muted-foreground-1",
    wert: "flex-1 min-w-0 text-sm text-foreground",
    status: "basis-full ps-34 empty:hidden",
    fehler: "basis-full ps-34",
    ganz: "basis-full mt-1",
    hilfe: "basis-full mt-1 text-xs text-muted-foreground-1",
  },
  oben: {
    zeile: "flex flex-col gap-y-1",
    label: "flex items-center text-xs text-muted-foreground-1",
    wert: "min-w-0 text-sm text-foreground",
    status: "empty:hidden",
    fehler: "",
    ganz: "mt-1",
    hilfe: "mt-1 text-xs text-muted-foreground-1",
  },
  standard: {
    zeile: "py-2 grid grid-cols-3 gap-x-3 gap-y-1 items-center",
    label: "col-span-1 flex items-center gap-x-2 text-sm text-muted-foreground-1",
    wert: "col-span-2 min-w-0 text-sm text-foreground",
    status: "col-span-2 col-start-2 empty:hidden",
    fehler: "col-span-2 col-start-2",
    ganz: "col-span-3 mt-1",
    hilfe: "col-span-3 mt-1 text-xs text-muted-foreground-1",
  },
  breit: {
    zeile: "py-2 grid grid-cols-1 sm:grid-cols-5 gap-x-3 gap-y-1 items-center",
    label: "sm:col-span-2 flex items-center gap-x-2 text-sm text-muted-foreground-1",
    wert: "sm:col-span-3 min-w-0 text-sm text-foreground",
    status: "sm:col-span-3 sm:col-start-3 empty:hidden",
    fehler: "sm:col-span-3 sm:col-start-3",
    ganz: "sm:col-span-5 mt-1",
    hilfe: "sm:col-span-5 mt-1 text-xs text-muted-foreground-1",
  },
};

export function Eigenschaftszeile({
  id,
  label,
  symbol,
  speicher = "ruhe",
  fehler,
  hinweissatz,
  hilfe,
  labelBreite = "standard",
  children,
}: {
  /** Eindeutige Id (verknüpft Beschriftung und Meldungen). */
  id: string;
  label: string;
  /** Symbol links (Lucide, `aria-hidden`), nur Schmuck. */
  symbol?: React.ReactNode;
  speicher?: SpeicherZustand;
  fehler?: string;
  hinweissatz?: string;
  /** Erklärtext zur Zeile: sichtbarer Text unter dem Wert (kein Tooltip), per `aria-describedby` verknüpft; der Aufrufer entscheidet, wann er ihn zeigt. */
  hilfe?: string;
  /** Spaltenaufteilung, Standard `standard` (1 : 2); `breit` (2 : 3, mobil untereinander) für lange Beschriftungen. */
  labelBreite?: LabelBreite;
  children: React.ReactNode;
}) {
  const zeigeHinweis = Boolean(hinweissatz) && !fehler;
  const a = AUFTEILUNG[labelBreite];
  return (
    <>
      {/* Preline: productivity/work-management/projects.html#project-details */}
      <div className={a.zeile} role="group" aria-labelledby={`${id}-label`} aria-describedby={hilfe ? `${id}-hilfe` : undefined}>
        <div className={a.label}>
          {symbol && labelBreite !== "oben" && <span className="shrink-0 size-4 inline-flex items-center justify-center" aria-hidden="true">{symbol}</span>}
          <span id={`${id}-label`} className="min-w-0">{label}</span>
        </div>
        <div className={a.wert}>{children}</div>
        <div className={a.status} role="status" aria-live="polite">
          {speicher === "speichert" && (
            <span className="inline-flex items-center gap-x-1.5 text-xs text-muted-foreground-1">
              <svg className="shrink-0 size-3 animate-spin" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              Speichert …
            </span>
          )}
          {speicher === "gespeichert" && (
            <span className="inline-flex items-center gap-x-1.5 text-xs text-muted-foreground-1">
              <svg className="shrink-0 size-3" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 6 9 17l-5-5" />
              </svg>
              Gespeichert
            </span>
          )}
        </div>
        {hilfe && (
          <p id={`${id}-hilfe`} className={a.hilfe}>
            {hilfe}
          </p>
        )}
        {fehler && (
          <p className={a.fehler}>
            <Zustandspille ton="gefahr" mitPunkt>{fehler}</Zustandspille>
          </p>
        )}
        {zeigeHinweis && (
          <div className={a.ganz}>
            <Alert ton="warnung" id={`${id}-hinweis`}>{hinweissatz}</Alert>
          </div>
        )}
      </div>
    </>
  );
}
